import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const productCategories = [
    { title: 'Furniture', to: '/products#furniture' },
    { title: 'Serveware & Kitchenware', to: '/products#serveware' },
    { title: 'Table Decor & Utility', to: '/products#table-decor' },
    { title: 'Wall Decor', to: '/products#wall-decor' }
  ];

  const navLinks = [
    { title: 'Home', to: '/' },
    { title: 'Products', to: '/products', hasDropdown: true },
    { title: 'About', to: '/about' },
    { title: 'Contact', to: '/contact' }
  ];

  return (
    <motion.nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-lg shadow-md' 
          : location.pathname === '/' || location.pathname === '/about'
            ? 'bg-transparent'
            : location.pathname === '/products'
              ? 'bg-white/80 backdrop-blur-lg'
              : location.pathname === '/contact'
                ? 'bg-gradient-to-br from-[#FAF3E0] via-amber-50 to-rose-50'
                : 'bg-white/80 backdrop-blur-lg shadow-md'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16 md:h-20">
          {/* Logo */}
          <motion.div
            className="flex-shrink-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/" className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-playfair ${
              isScrolled || (location.pathname !== '/' && location.pathname !== '/about')
                ? 'text-[#B38053]' 
                : 'text-white'
            }`}>
              ANG
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-3 lg:space-x-6 xl:space-x-8">
            {navLinks.map((link) => (
              <motion.div 
                key={link.title}
                className="relative"
                onMouseEnter={() => link.hasDropdown && setIsProductsDropdownOpen(true)}
                onMouseLeave={() => link.hasDropdown && setIsProductsDropdownOpen(false)}
              >
                <Link
                  to={link.to}
                  className={`transition-all duration-300 text-sm lg:text-base ${
                    isScrolled || (location.pathname !== '/' && location.pathname !== '/about')
                      ? location.pathname === '/contact' && !isScrolled
                        ? 'text-[#8B4513] hover:text-[#A0522D]'
                        : 'text-[#B38053] hover:text-[#96663D]'
                      : 'text-white hover:text-gray-200'
                  } ${location.pathname === link.to ? 'font-semibold' : ''}`}
                >
                  <motion.span
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-block"
                  >
                    {link.title}
                  </motion.span>
                </Link>

                {/* Products Dropdown */}
                {link.hasDropdown && (
                  <AnimatePresence>
                    {isProductsDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg py-2 z-50"
                      >
                        {productCategories.map((category) => (
                          <Link
                            key={category.title}
                            to={category.to}
                            className="block px-4 py-2.5 text-sm text-[#B38053] hover:text-[#96663D] hover:bg-[#F8F6F2] transition-all duration-300"
                          >
                            {category.title}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </motion.div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-1.5 sm:p-2 rounded-md transition-colors duration-300 ${
                isScrolled || (location.pathname !== '/' && location.pathname !== '/about')
                  ? location.pathname === '/contact' && !isScrolled
                    ? 'text-[#8B4513] hover:text-[#A0522D]'
                    : 'text-[#B38053] hover:text-[#96663D]'
                  : 'text-white hover:text-gray-200'
              }`}
              aria-label="Toggle menu"
            >
              <svg
                className="h-5 w-5 sm:h-6 sm:w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden bg-white/95 backdrop-blur-sm shadow-lg absolute w-full"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-3 sm:px-4 pt-2 pb-3 space-y-1 sm:space-y-2">
              {navLinks.map((link) => (
                <motion.div key={link.title}>
                  <Link
                    to={link.to}
                    className={`block px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-medium text-[#B38053] hover:text-[#96663D] hover:bg-[#F8F6F2] transition-all duration-300 ${
                      location.pathname === link.to ? 'font-semibold bg-[#F8F6F2]' : ''
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <motion.span 
                      whileHover={{ x: 10 }} 
                      className="flex items-center"
                    >
                      {link.title}
                    </motion.span>
                  </Link>
                  {/* Mobile Products Dropdown */}
                  {link.hasDropdown && (
                    <div className="ml-4 mt-1 space-y-1">
                      {productCategories.map((category) => (
                        <Link
                          key={category.title}
                          to={category.to}
                          className="block px-3 sm:px-4 py-2 text-sm text-[#B38053]/80 hover:text-[#96663D] hover:bg-[#F8F6F2] rounded-lg transition-all duration-300"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {category.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
 